import React, { useState, useEffect } from 'react';
import { getCostBenefit } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' },
  sub: { fontSize: '14px', color: '#718096', marginBottom: '16px' },
  framing: { fontSize: '13px', color: '#22543d', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '6px', padding: '12px 14px', marginBottom: '20px', lineHeight: '1.5' },
  headline: { fontSize: '15px', color: '#2d3748', background: '#faf5ff', border: '1px solid #d6bcfa', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', fontWeight: '600' },
  note: { fontSize: '13px', color: '#744210', background: '#fffaf0', border: '1px solid #f6e05e', borderRadius: '6px', padding: '10px 14px', marginBottom: '20px' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' },
  card: { background: '#fff', padding: '18px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardLabel: { fontSize: '12px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  cardValue: { fontSize: '26px', fontWeight: '700', color: '#1a202c' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#2d3748', margin: '24px 0 12px' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  scopeBar: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  scopeLabel: { fontSize: '15px', fontWeight: '700', color: '#1a202c' },
  backBtn: { padding: '6px 14px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  archivedTag: { marginLeft: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: '#e2e8f0', color: '#4a5568' },
  rowBtn: { padding: '4px 10px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
};

const usd = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lev = (n) => (n === null || n === undefined) ? '-' : n + '×';

export default function CostBenefitPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // null = aggregate (all batches); otherwise the batch number being viewed.
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    getCostBenefit()
      .then((r) => { if (r.error) setError(r.error); else setData(r); })
      .catch(() => setError('Failed to load the AI grading impact data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div><h1 style={styles.heading}>AI Grading Impact</h1><p style={styles.sub}>Loading...</p></div>;

  const a = data?.assumptions || {};
  // Top stats show either the aggregate totals (default) or a single selected
  // batch. The endpoint already returns every batch's full numbers, so the
  // drill-down is pure client-side state - no extra fetch.
  const selected = selectedBatch
    ? (data?.per_batch || []).find((b) => String(b.batch) === String(selectedBatch))
    : null;
  const t = selected || data?.totals || {};
  const viewingBatch = !!selected;

  return (
    <div>
      <h1 style={styles.heading}>AI Grading Impact</h1>
      <p style={styles.sub}>How much skilled-trainer time the AI grader handed back to the team, and what that capacity cost.</p>
      {error && <div style={styles.error}>{error}</div>}

      {data && (
        <>
          <div style={styles.framing}>
            <strong>No trainers were reduced.</strong> This shows the trainer time the AI grader freed up - hours that were
            redirected from repetitive grading to higher-value work like coaching and engaging trainees - and what that freed
            capacity cost the company.
          </div>

          {data.excluded_test_count > 0 && (
            <div style={{ fontSize: '13px', color: '#744210', background: '#fffaf0', border: '1px solid #f6e05e', borderRadius: '6px', padding: '10px 14px', marginBottom: '20px' }}>
              <strong>{data.excluded_test_count}</strong> test submission{data.excluded_test_count !== 1 ? 's' : ''} (submitted while Test Mode was on) {data.excluded_test_count !== 1 ? 'are' : 'is'} excluded from every figure on this page.
            </div>
          )}

          <div style={styles.scopeBar}>
            <span style={styles.scopeLabel}>
              {viewingBatch
                ? <>Showing Batch {selected.batch}{selected.archived ? <span style={styles.archivedTag}>archived</span> : null}</>
                : 'Showing all batches (aggregate)'}
            </span>
            {viewingBatch && (
              <button style={styles.backBtn} onClick={() => setSelectedBatch(null)}>&larr; Back to all batches</button>
            )}
          </div>

          {t.leverage !== null && t.leverage !== undefined && t.graded_count > 0 && (
            <div style={styles.headline}>
              {viewingBatch ? <>For Batch {selected.batch}: for</> : 'For'} every $1 spent on AI grading, about <strong>{usd(t.leverage)}</strong>{' '}
              of skilled-trainer time was freed for coaching and trainee engagement
              {' '}({lev(t.leverage)} leverage).
            </div>
          )}

          <div style={styles.note}>
            Assumptions: <strong>{a.minutes_per_note} min</strong> average manual grading time per note,
            <strong> ${a.hourly_rate}/hr</strong> grader rate, Claude pricing
            <strong> ${a.input_price_per_million}/M</strong> input and <strong>${a.output_price_per_million}/M</strong> output tokens.
            {t.estimated_count > 0 && (
              <> Token usage is tracked per grading going forward; <strong>{t.estimated_count}</strong> of {t.graded_count} graded
              submissions predate token tracking. Where the real billed cost is known (e.g. a batch graded under a different API
              key) it is entered manually as the actual cost instead of an estimate.</>
            )}
          </div>

          <div style={styles.cards}>
            <div style={styles.card}><div style={styles.cardLabel}>Graded Submissions</div><div style={styles.cardValue}>{t.graded_count}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>Trainer Hours Reclaimed</div><div style={styles.cardValue}>{t.hours_reclaimed}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>Man-Years Reclaimed</div><div style={styles.cardValue}>{t.man_years}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>= Full 40-hr Weeks</div><div style={styles.cardValue}>{t.fte_weeks}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>Value of Reclaimed Time</div><div style={{ ...styles.cardValue, color: '#276749' }}>{usd(t.value_reclaimed)}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>AI Grading Cost</div><div style={{ ...styles.cardValue, color: '#9C3D97' }}>{usd(t.compute_cost)}</div></div>
            <div style={styles.card}><div style={styles.cardLabel}>Leverage</div><div style={{ ...styles.cardValue, color: '#276749' }}>{lev(t.leverage)}</div></div>
          </div>

          <div style={styles.sectionTitle}>By batch <span style={{ fontWeight: '400', fontSize: '13px', color: '#a0aec0' }}>(select a batch to see its impact above)</span></div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Batch</th>
                <th style={styles.th}>Graded</th>
                <th style={styles.th}>Hours Reclaimed</th>
                <th style={styles.th}>Value of Reclaimed Time</th>
                <th style={styles.th}>AI Grading Cost</th>
                <th style={styles.th}>Leverage</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {[...data.per_batch].sort((x, y) => {
                // Newest batch first. Numeric when possible; non-numeric
                // labels (e.g. "(none)") sort to the bottom.
                const nx = Number(x.batch), ny = Number(y.batch);
                if (isNaN(nx) && isNaN(ny)) return 0;
                if (isNaN(nx)) return 1;
                if (isNaN(ny)) return -1;
                return ny - nx;
              }).map((b, i) => {
                const isSel = String(b.batch) === String(selectedBatch);
                return (
                  <tr key={i}
                    onClick={() => setSelectedBatch(b.batch)}
                    style={{ cursor: 'pointer', background: isSel ? '#faf5ff' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = '#f7fafc'; }}
                    onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ ...styles.td, fontWeight: '600' }}>
                      Batch {b.batch}{b.cost_override ? ' (actual cost)' : (b.estimated_count > 0 ? ' *' : '')}
                      {b.archived ? <span style={styles.archivedTag}>archived</span> : null}
                    </td>
                    <td style={styles.td}>{b.graded_count}</td>
                    <td style={styles.td}>{b.hours_reclaimed}</td>
                    <td style={styles.td}>{usd(b.value_reclaimed)}</td>
                    <td style={styles.td}>{usd(b.compute_cost)}</td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#276749' }}>{lev(b.leverage)}</td>
                    <td style={styles.td}>
                      <button style={styles.rowBtn} onClick={(e) => { e.stopPropagation(); setSelectedBatch(b.batch); }}>View</button>
                    </td>
                  </tr>
                );
              })}
              {data.per_batch.length === 0 && (
                <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={7}>No graded submissions yet.</td></tr>
              )}
            </tbody>
          </table>
          {data.per_batch.some((b) => b.estimated_count > 0 && !b.cost_override) && (
            <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px' }}>* includes submissions graded before token tracking (AI grading cost estimated).</p>
          )}
          {data.per_batch.some((b) => b.cost_override) && (
            <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>(actual cost) = real billed AI grading cost entered manually (graded under a different API key, before token tracking).</p>
          )}
        </>
      )}
    </div>
  );
}
